<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{
      'inquiries-check-popup': true,
      'is-visible': isVisible
    }">
    <h2>
      {{ $t('inquiriesCheckPopup.title') }} ({{ inquiries.length }})
    </h2>

    <small v-html="$t('inquiriesCheckPopup.changesWillBeAppliedDuringSave')"></small>

    <div class="inquiries-to-check-wrapper">
      <div class="inquiry-to-check"
        v-for="(inquiry, index) of inquiries"
        :key="'inquiry-to-check-' + index">
        <reservation
          :reservationHoursFirst="true"
          :inCalendar="true"
          :isBlock="false"
          :isReservation="true"
          :forceShowDate="true"
          :showReservationID="true"
          :showWorker="false"
          :showActions="false"
          :reservationData="inquiry" />
        <div
          :class="{
            'inquiry-to-check-status': true,
            'action-required': true,
            'is-changed': !!changes[inquiry.id],
            'is-cancelled': changes[inquiry.id] && changes[inquiry.id] === 'status:cancel',
            'is-without-changes': changes[inquiry.id] && changes[inquiry.id] === 'status:no-changes'
          }">
          <template v-if="openedForms.workers.indexOf(inquiry.id) === -1 && openedForms.dates.indexOf(inquiry.id) === -1 && !changes[inquiry.id]">
            <small>
              {{ $t('inquiriesCheckPopup.actionRequired') }}:
            </small>
            <btn
              type="small with-icon"
              iconName="profile"
              iconSize="xxs"
              @click.native="openForm('workers', inquiry)">
              {{ $t('inquiriesCheckPopup.changeWorker') }}
            </btn>
            <btn
              type="small with-icon"
              iconName="calendar-big"
              iconSize="xxxs"
              @click.native="openForm('dates', inquiry)">
              {{ $t('inquiriesCheckPopup.changeTerm') }}
            </btn>
            <btn
              type="danger small with-icon"
              iconName="cross"
              iconSize="xxxs"
              iconColor="color-light"
              @click.native="setChanges(inquiry.id, 'status:cancel')">
              {{ $t('reservation.cancelReservation') }}
            </btn>
            <btn
              type="blue small with-icon"
              iconName="ok"
              iconSize="xxxs"
              iconColor="color-light"
              @click.native="setChanges(inquiry.id, 'status:no-changes')">
              {{ $t('inquiriesCheckPopup.noChanges') }}
            </btn>
          </template>

          <div v-if="changes[inquiry.id] && changes[inquiry.id] === 'status:cancel'">
            <small>
              <strong>
                {{ $t('inquiriesCheckPopup.reservationWillBeCancelled') }}
              </strong>

              <a
                href="#"
                @click.prevent="resetChanges(inquiry.id)">
                {{ $t('inquiriesCheckPopup.revert') }}
              </a>
            </small>
          </div>

          <div v-if="changes[inquiry.id] && changes[inquiry.id] === 'status:no-changes'">
            <small>
              <strong>
                {{ $t('inquiriesCheckPopup.reservationWillBeLeftWithoutChanges') }}
              </strong>

              <a
                href="#"
                @click.prevent="resetChanges(inquiry.id)">
                {{ $t('inquiriesCheckPopup.revert') }}
              </a>
            </small>
          </div>

          <div v-if="changes[inquiry.id] && changes[inquiry.id].indexOf('worker:') > -1">
            <small>
              {{ $t('inquiriesCheckPopup.workerWillBeChangedTo') }}

              <strong>
                {{ getWorkerName(inquiry.id) }}
              </strong>

              <a
                href="#"
                @click.prevent="resetChanges(inquiry.id)">
                {{ $t('inquiriesCheckPopup.revert') }}
              </a>
            </small>
          </div>

          <div v-if="changes[inquiry.id] && changes[inquiry.id].indexOf('date:') > -1">
            <small>
              {{ $t('inquiriesCheckPopup.dateWillBeChangedTo') }}

              <strong>
                {{ getNewDate(inquiry.id) }}
              </strong>

              <a
                href="#"
                @click.prevent="resetChanges(inquiry.id)">
                {{ $t('inquiriesCheckPopup.revert') }}
              </a>
            </small>
          </div>

          <div
            v-if="openedForms.workers.indexOf(inquiry.id) > -1"
            class="inquiry-to-check-worker-form">
            <strong class="inquiry-to-check-worker-form-label">
              {{ $t('inquiriesCheckPopup.changeWorkerTo') }}:
            </strong>

            <select
              v-model="workersForm[inquiry.id]"
              @change="checkTerm('worker', inquiry)">
              <option key="worker-0" :value="undefined">{{ $t('inquiriesCheckPopup.selectWorker') }}</option>
              <option
                v-for="workerData of workers"
                :key="'worker-' + workerData.id"
                :value="workerData.id">
                {{ workerData.name }}
              </option>
            </select>

            <p
              v-if="workersForm[inquiry.id]"
              :class="{
                'text-center': true,
                'text-danger': !checkTermStatus[inquiry.id] && !checkTermLoading[inquiry.id],
                'text-success': checkTermStatus[inquiry.id] && !checkTermLoading[inquiry.id]
              }">
              {{ $t('reservationFormCustomHour.selectedTerm') }}
              <strong>
                {{ inquiry.date }} {{ $t('reservationFormCustomHour.selectedHour') }} {{ inquiry.time_from }}
              </strong>
              <span
                v-if="checkTermMessage[inquiry.id] && !checkTermLoading[inquiry.id]"
                class="check-term-message"
                v-pure-html="checkTermMessage[inquiry.id]"
                style="display: block;">
              </span>
            </p>

            <div class="inquiry-to-check-worker-form-actions">
              <btn
                type="danger"
                @click.native="closeForm('workers', inquiry.id)">
                {{ $t('common.ui.cancel') }}
              </btn>

              <btn
                :disabled="!workersForm[inquiry.id]"
                type="success"
                @click.native="setChanges(inquiry.id, 'worker:' + workersForm[inquiry.id]); closeForm('workers', inquiry.id)">
                {{ $t('inquiriesCheckPopup.changeWorker') }}
              </btn>
            </div>
          </div>

          <div
            v-if="openedForms.dates.indexOf(inquiry.id) > -1"
            class="inquiry-to-check-date-form">
            <strong class="inquiry-to-check-date-form-label">
              {{ $t('inquiriesCheckPopup.changeDateTo') }}:
            </strong>

            <div class="inquiry-to-check-date-form-values">
              <date-picker
                slot="field"
                :language="$t('common.ui.datepickerLanguage')"
                :full-month-name="true"
                ref="date-picker"
                :value="datesForm[inquiry.id]"
                @selected="changeDate($event, inquiry)"
                :format="$t('common.ui.datepickerFormat')"
                :monday-first="true"></date-picker>

              <select
                slot="field"
                class="small-select"
                v-model="hoursForm[inquiry.id]"
                @change="checkTerm('date', inquiry)">
                <option
                  v-for="(i, index) of Array(24)"
                  :key="'select-h-' + ~~i + '-' + index"
                  :value="outputValue(index)">
                  {{ outputValue(index) }}
                </option>
              </select>

              <select
                slot="field"
                class="small-select"
                v-model="minutesForm[inquiry.id]"
                @change="checkTerm('date', inquiry)">
                <option
                  v-for="(i, index) of Array(12)"
                  :key="'select-m-' + ~~i + '-' + index * 5"
                  :value="outputValue(index * 5)">
                  {{ outputValue(index * 5) }}
                </option>
              </select>
            </div>

            <p
              v-if="datesForm[inquiry.id]"
              :class="{
                'text-center': true,
                'text-danger': !checkTermStatus[inquiry.id] && !checkTermLoading[inquiry.id],
                'text-success': checkTermStatus[inquiry.id] && !checkTermLoading[inquiry.id]
              }">
              {{ $t('reservationFormCustomHour.selectedTerm') }}
              <strong>
                {{ datesForm[inquiry.id] }} {{ $t('reservationFormCustomHour.selectedHour') }} {{ hoursForm[inquiry.id] }}:{{ minutesForm[inquiry.id] }}
              </strong>
              <span
                v-if="checkTermMessage[inquiry.id] && !checkTermLoading[inquiry.id]"
                class="check-term-message"
                v-pure-html="checkTermMessage[inquiry.id]"
                style="display: block;">
              </span>
            </p>

            <div class="inquiry-to-check-date-form-actions">
              <btn
                type="danger"
                @click.native="closeForm('dates', inquiry.id)">
                {{ $t('common.ui.cancel') }}
              </btn>

              <btn
                type="success"
                @click.native="setChanges(inquiry.id, 'date:' + datesForm[inquiry.id] + ' ' + hoursForm[inquiry.id] + ':' + minutesForm[inquiry.id]); closeForm('dates', inquiry.id)"
                :disabled="!datesForm[inquiry.id]">
                {{ $t('inquiriesCheckPopup.changeTerm') }}
              </btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="inquiries-to-check-summary">
      <div
        v-if="changesCounterWorker || changesCounterDate || changesCounterCancel || changesCounterNoChange"
        class="inquiries-to-check-summary-stats">
        <strong>{{ $t('inquiriesCheckPopup.changesSummary.title') }}:</strong>
        <div v-if="changesCounterWorker > 0">{{ $t('inquiriesCheckPopup.changesSummary.worker') }}: {{ changesCounterWorker }}</div>
        <div v-if="changesCounterDate > 0">{{ $t('inquiriesCheckPopup.changesSummary.date') }}: {{ changesCounterDate }}</div>
        <div v-if="changesCounterCancel > 0">{{ $t('inquiriesCheckPopup.changesSummary.cancel') }}: {{ changesCounterCancel }}</div>
        <div v-if="changesCounterNoChange > 0">{{ $t('inquiriesCheckPopup.changesSummary.noChanges') }}: {{ changesCounterNoChange }}</div>
      </div>

      <div class="inquiries-to-check-summary-notify">
        <switcher
          slot="field"
          key="notify-client-switcher"
          :disabled="loadingData"
          v-model="notify" />

        <label slot="label">
          {{ $t('inquiriesCheckPopup.notifyClients') }}
        </label>
      </div>
    </div>

    <btn
      @click.native="saveChanges"
      :disabled="loadingData || !allInquiriesModified"
      type="popup-left primary">
      <template v-if="allInquiriesModified">
        <template v-if="!loadingData">{{ $t('inquiriesCheckPopup.confirmChanges') }}</template>
        <template v-if="loadingData">{{ $t('inquiriesCheckPopup.saving') }}</template>
      </template>
      <template v-else>
        {{ $t('inquiriesCheckPopup.waitingForChange') }}: {{ leftToChange }}
      </template>
    </btn>

    <btn
      @click.native="cancel"
      type="popup-right">
      {{ $t('common.ui.cancel') }}
    </btn>
  </div>
</template>

<script>
import Vue from 'vue';
import Configuration from './../mixins/Configuration';
import DatePicker from 'vuejs-datepicker';
import PopupZindex from './../mixins/PopupZindex';
import Reservation from './../ui/Reservation';
import ErrorUtils from './../helpers/ErrorUtils.js';

export default {
  name: 'inquiries-check-popup',
  mixins: [
    Configuration,
    PopupZindex
  ],
  components: {
    'reservation': Reservation,
    'date-picker': DatePicker
  },
  computed: {
    allInquiriesModified () {
      let changedIDs = Object.keys(this.changes);
      return this.inquiries.length - changedIDs.length === 0;
    },
    leftToChange () {
      let changedIDs = Object.keys(this.changes);
      return this.inquiries.length - changedIDs.length;
    },
    changesCounterWorker () {
      let changedIDs = Object.keys(this.changes);
      let counter = 0;

      for (let i = 0; i < changedIDs.length; i++) {
        let id = changedIDs[i];

        if (this.changes[id].indexOf('worker:') > -1) {
          counter++;
        }
      }

      return counter;
    },
    changesCounterDate () {
      let changedIDs = Object.keys(this.changes);
      let counter = 0;

      for (let i = 0; i < changedIDs.length; i++) {
        let id = changedIDs[i];

        if (this.changes[id].indexOf('date:') > -1) {
          counter++;
        }
      }

      return counter;
    },
    changesCounterCancel () {
      let changedIDs = Object.keys(this.changes);
      let counter = 0;

      for (let i = 0; i < changedIDs.length; i++) {
        let id = changedIDs[i];

        if (this.changes[id].indexOf('status:cancel') > -1) {
          counter++;
        }
      }

      return counter;
    },
    changesCounterNoChange () {
      let changedIDs = Object.keys(this.changes);
      let counter = 0;

      for (let i = 0; i < changedIDs.length; i++) {
        let id = changedIDs[i];

        if (this.changes[id].indexOf('status:no-changes') > -1) {
          counter++;
        }
      }

      return counter;
    }
  },
  data () {
    return {
      cancelledInquiries: [],
      changes: {},
      datesForm: {},
      hoursForm: {},
      minutesForm: {},
      inquiries: [],
      isVisible: false,
      loadingData: false,
      openedForms: {
        dates: [],
        workers: []
      },
      currentWorker: 0,
      checkTermLoading: {},
      checkTermMessage: {},
      checkTermStatus: {},
      notCheckInquiries: [],
      notify: false,
      workers: [],
      workersForm: {},
      workersLoaded: false
    };
  },
  mounted () {
    this.$bus.$on('show-inquiries-check-popup', this.show);
  },
  methods: {
    show (data) {
      Vue.set(this, 'changes', {});
      Vue.set(this, 'workersForm', {});
      Vue.set(this, 'checkTermLoading', {});
      Vue.set(this, 'checkTermMessage', {});
      Vue.set(this, 'checkTermStatus', {});
      Vue.set(this, 'inquiries', data.inquiries);
      Vue.set(this.openedForms, 'workers', []);
      Vue.set(this.openedForms, 'dates', []);
      Vue.set(this, 'currentWorker', data.workerID);
      Vue.set(this, 'notCheckInquiries', data.notCheckInquiries)
      this.isVisible = true;
      this.increasePopupZindex();
      this.workersLoaded = false;
      this.loadWorkers();
    },
    hide () {
      Vue.set(this, 'changes', {});
      Vue.set(this, 'inquiries', []);
      Vue.set(this, 'checkTermLoading', {});
      Vue.set(this, 'checkTermMessage', {});
      Vue.set(this, 'checkTermStatus', {});
      Vue.set(this.openedForms, 'workers', []);
      Vue.set(this.openedForms, 'dates', []);
      Vue.set(this, 'currentWorker', 0);
      Vue.set(this, 'notCheckInquiries', []);
      this.isVisible = false;
      this.loadingData = false;
      this.workersLoaded = false;
      this.removePopupZindex();
    },
    saveChanges () {
      this.loadingData = true;
      let dataToSave = [];
      let noChanges = [];

      if (this.notCheckInquiries) {
        noChanges = JSON.parse(JSON.stringify(this.notCheckInquiries));
      }

      for (let i = 0; i < this.inquiries.length; i++) {
        let id = this.inquiries[i].id;
        let changeToSave = this.changes[id];
        let dataToAdd = {
          inquiry_id: id
        };

        if (changeToSave.indexOf('worker:') > -1) {
          let workerID = parseInt(changeToSave.replace('worker:', ''), 10);
          dataToAdd.worker_id = workerID;
          dataToSave.push(dataToAdd);
        } else if (changeToSave.indexOf('date:') > -1) {
          let dateTime = changeToSave.replace('date:', '');
          dataToAdd.date = dateTime.split(' ')[0];
          dataToAdd.hour = dateTime.split(' ')[1];
          dataToSave.push(dataToAdd);
        } else if (changeToSave === 'status:cancel') {
          dataToAdd.status = 5;
          dataToSave.push(dataToAdd);
        } else if (changeToSave === 'status:no-changes') {
          noChanges.push(id);
        }
      }

      if (dataToSave.length) {
        this.$http.post('/panel/reservations/change-main-data', {
          inquiries: dataToSave,
          notify: +this.notify
        }).then(response => {
          if (response.data) {
            if (response.data.filter(status => status.success === 0).length) {
              let inquiriesWithIssues = response.data.filter(status => status.success === 0).map(inquiry => inquiry.inquiry_id);
              this.inquiries = this.inquiries.filter(inquiry => inquiriesWithIssues.indexOf(inquiry.id) > -1);

              for (let i = 0; i < inquiriesWithIssues.length; i++) {
                Vue.delete(this.changes, inquiriesWithIssues[i]);
              }

              this.$bus.$emit('alert-display', {
                title: window.bookero.translations.$t('inquiriesCheckPopup.changeIssueTitle'),
                desc: window.bookero.translations.$t('errors.operationError.changeIssueDesc'),
                okLabel: window.bookero.translations.$t('common.ui.ok')
              });
            } else {
              this.$bus.$emit('opening-hours-worker-popup-save', noChanges);
              this.cancel();
            }
          }

          this.loadingData = false;
        }).catch(error => {
          this.loadingData = false;
          ErrorUtils.handleLoadError(error, '/panel/reservations/change-main-data', this.$router, this.$bus);
        });
      } else {
        this.$bus.$emit('opening-hours-worker-popup-save', noChanges);
        this.cancel();
      }
    },
    cancel () {
      this.hide();
    },
    loadWorkers () {
      this.$http.post('/panel/workers/get-workers?short_list=1').then(response => {
        this.workers = response.data.users.filter(worker => worker.worker_type === 0 && worker.id !== this.currentWorker);
        this.workersLoaded = true;
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/workers/get-workers?short_list=1', this.$router, this.$bus);
      });
    },
    setChanges (inquiryID, changeValue) {
      Vue.set(this.changes, inquiryID, changeValue);
    },
    resetChanges (inquiryID) {
      Vue.delete(this.changes, inquiryID);
    },
    openForm (formType, inquiry) {
      this.openedForms[formType].push(inquiry.id);

      if (formType === 'dates') {
        Vue.set(this.datesForm, inquiry.id, inquiry.date);
        Vue.set(this.hoursForm, inquiry.id, inquiry.time_from.split(':')[0] < 10 ? '0' + inquiry.time_from.split(':')[0] : inquiry.time_from.split(':')[0]);
        Vue.set(this.minutesForm, inquiry.id, inquiry.time_from.split(':')[1]);
      }
    },
    closeForm (formType, inquiryID) {
      Vue.set(this.openedForms, formType, this.openedForms[formType].filter(item => item !== inquiryID));
    },
    getNewDate (inquiryID) {
      return this.changes[inquiryID].replace('date:', '');
    },
    getWorkerName (inquiryID) {
      let workerID = parseInt(this.changes[inquiryID].replace('worker:', ''));
      let workerName = this.workers.filter(worker => worker.id === workerID)

      if (workerName[0]) {
        return workerName[0].name;
      }

      return '';
    },
    changeDate (newValue, inquiry) {
      let month = newValue.getMonth() + 1.0;
      let day = newValue.getDate();

      if (month < 10) {
        month = '0' + month;
      }

      if (day < 10) {
        day = '0' + day;
      }

      Vue.set(this.datesForm, inquiry.id, [newValue.getFullYear(), month, day].join('-'));
      this.checkTerm('date', inquiry);
    },
    outputValue (value) {
      return value < 10 ? '0' + value : value;
    },
    checkTerm (checkType, inquiry) {
      let config = {};
      this.checkTermLoading[inquiry.id] = true;
      config.inquiry_id = parseInt(inquiry.id, 10);

      if (inquiry.confirm_email !== '') {
        config.email = inquiry.confirm_email;
      }

      if (inquiry.phone !== '') {
        config.phone = inquiry.phone;
      }

      if (inquiry.people_number !== '') {
        config.people = inquiry.people_number;
      }

      if (inquiry.service) {
        config.service = inquiry.service.id;
      }

      if (this.workersEnabled && inquiry.worker) {
        config.worker = inquiry.worker.id;
      }

      if (checkType === 'worker') {
        config.worker = this.workersForm[inquiry.id];
      }

      config.date = inquiry.date;
      config.start_date = inquiry.date;
      config.plugin_comment = inquiry.plugin_comment;
      config.modified_time_increaser = 5;
      config.duration = inquiry.duration;
      config.hour = inquiry.time_from.length === 4 ? '0' + inquiry.time_from : inquiry.time_from;

      if (checkType === 'date') {
        config.date = this.datesForm[inquiry.id];
        config.start_date = this.datesForm[inquiry.id];
        config.hour = this.hoursForm[inquiry.id] + ':' + this.minutesForm[inquiry.id];
      }

      let endpoint = '/panel/reservations/check-hourly-date';
      Vue.set(this.checkTermMessage, inquiry.id, '');
      Vue.set(this.checkTermStatus, inquiry.id, true);

      this.$http.post(endpoint, config).then(response => {
        if (!response.data.data.length) {
          Vue.set(this.checkTermMessage, inquiry.id, response.data.message);
          Vue.set(this.checkTermStatus, inquiry.id, false);
        }

        Vue.set(this.checkTermMessage, inquiry.id, response.data.data.message);
        Vue.set(this.checkTermStatus, inquiry.id, !!response.data.data.valid);
        Vue.set(this.checkTermLoading, inquiry.id, false);
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, endpoint, this.$router, this.$bus, this.checkTerm.bind(this));
        Vue.set(this.checkTermLoading, inquiry.id, false);
        Vue.set(this.checkTermMessage, inquiry.id, '');
        Vue.set(this.checkTermStatus, inquiry.id, false);
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-inquiries-check-popup', this.show);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.inquiries-check-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  max-width: 94%;
  padding: 3rem 4rem 6rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 800px;

  &.is-visible {
    display: block;
  }

  h2 {
    font-weight: 400;
    line-height: 1;
    margin: 0 0 2rem 0;
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }

  .inquiries-to-check-wrapper {
    margin-top: 2rem;
    max-height: calc(var(--doc-height) - 240px);
    overflow-y: auto;
  }

  .reservation.is-row {
    padding: .5rem 1rem;
  }

  .inquiry-to-check-status {
    align-items: center;
    background: $color-bg-light;
    border-radius: 0 0 5px 5px;
    border: 1px solid $color-border-light;
    display: flex;
    margin-bottom: 1.5rem;
    padding: 5px 10px;

    small {
      padding-right: 1rem;
    }

    &.is-changed {
      background: lighten($color-success, 45%);
      border: 1px solid lighten($color-success, 30%);
      color: $color-success;
    }

    &.is-cancelled {
      background: lighten($color-danger, 40%);
      border: 1px solid lighten($color-danger, 30%);
      color: $color-danger;
    }

    &.is-without-changes {
      background: lighten($color-notice, 40%);
      border: 1px solid lighten($color-notice, 30%);
      color: $color-notice;
    }
  }

  .inquiry-to-check /deep/ .reservation.is-row {
    border-radius: 5px 5px 0 0;
  }

  .inquiry-to-check-date-form,
  .inquiry-to-check-worker-form {
    justify-content: center;
    padding: 1rem;
    text-align: center;
    width: 100%;

    select {
      max-width: 360px;
      width: 100%;
    }
  }

  .inquiry-to-check-date-form-label,
  .inquiry-to-check-worker-form-label {
    display: block;
    margin-bottom: 1rem;
  }

  .inquiry-to-check-date-form-actions,
  .inquiry-to-check-worker-form-actions {
    display: flex;
    justify-content: center;
    padding-top: 1rem;
  }

  .inquiry-to-check-date-form-values {
    display: flex;
    gap: 5px;
    justify-content: center;

    select {
      width: 72px;
    }
  }

  .check-term-message /deep/ ul {
    list-style-type: none;
    margin: 0;
    padding: 0;

    li {
      list-style-type: none;
      margin: 0;
      padding: 0;
      text-align: center;
    }
  }

  .inquiries-to-check-summary {
    display: flex;
    gap: 2rem;

    &-stats {
      display: flex;
      flex-wrap: wrap;
      font-size: 12px;
      width: 60%;

      strong {
        display: block;
        width: 100%;
      }

      div {
        width: 50%;
      }
    }

    &-notify {
      align-items: center;
      display: flex;
      justify-content: flex-end;
      margin-left: auto;
      width: 40%;

      label {
        padding-left: 1rem;
      }
    }
  }
}

@media (max-width: 840px) {
  .inquiries-check-popup {
    padding-left: 2rem;
    padding-right: 2rem;

    .inquiry-to-check-status.action-required {
      flex-wrap: wrap;

      small {
        display: block;
        margin-bottom: .5rem;
        width: 100%;
      }
    }
  }
}

@media (max-width: 720px) {
  .inquiries-check-popup {
    .inquiry-to-check .reservation {
      flex-direction: column;
      gap: .5rem;
      justify-content: center;
      text-align: center;

      & /deep/ > * {
        flex-direction: row;
        justify-content: center;
        padding: 0;
        text-align: center;
        width: 100%!important;
      }

      .duration {
        margin-left: auto;
      }
    }

    .inquiry-to-check-status.action-required {
      flex-wrap: wrap;
      gap: 1rem;
      text-align: center;

      button {
        justify-content: center;
        text-align: center;
        width: calc(50% - 1.1rem);
      }
    }
  }
}

@media (max-width: 480px) {
  .inquiries-check-popup {
    height: 100%;
    max-width: 100%;
    padding: 1.5rem 1.5rem 5rem 1.5rem;
    width: 100%;

    h2 {
      font-size: 2rem;
      margin-bottom: 1rem;
    }

    .inquiries-to-check-summary-notify label {
      font-size: 1.2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/InquiriesCheckPopup.vue